import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/pro-solid-svg-icons";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
} from "@mui/material";

import * as d from "shared/src/date.mjs";
import { status } from "shared/src/appointment.mjs";

import * as api from "../../services/api";
import ButtonLoader from "../ButtonLoader";
import { FormErrorList, useFormAction } from "../../form";
import { useCurrentLocation } from "../LocationSelector.jsx";
import AutocompleteSelect from "../AutocompleteSelect";
import { ClientAutocomplete } from "./ClientAutocomplete";
import { useServiceOptions } from "./hooks/useServiceOptions";
import { useProductOptions } from "./hooks/useProductOptions";
import { useLocationOptions } from "./hooks/useLocationOptions";
import { hasBlocker } from "shared/src/client.mjs";
import { useSnackbarMessage } from "../Snackbar.jsx";

const adhocTypesEnum = {
  STREET: "STREET",
  INITIALEXAMINATION: "INITIAL_EXAMINATION",
  NOSHOW: "NOSHOW",
  CHAIR: "CHAIR",
};

export const adhocTypes = {
  STREET: {
    name: "Z ulice",
    value: adhocTypesEnum.STREET,
    id: adhocTypesEnum.STREET,
  },
  INITIAL_EXAMINATION: {
    name: "Vstupní vyšetření",
    value: adhocTypesEnum.INITIALEXAMINATION,
    id: adhocTypesEnum.INITIALEXAMINATION,
  },
  NOSHOW: {
    name: "No Show",
    value: adhocTypesEnum.NOSHOW,
    id: adhocTypesEnum.NOSHOW,
  },
  CHAIR: {
    name: "Z křesla",
    value: adhocTypesEnum.CHAIR,
    id: adhocTypesEnum.CHAIR,
  },
};

/**
 * @typedef NewAdhocDialogDefaults
 *
 * @typedef NewAdhocDialogState
 * @property {object | null} appointment
 * @property {object | null} client
 * @property {object | null} serviceOption
 * @property {string[]} types
 */

/**
 * @param {object} props
 * @param {() => void} props.close
 * @param {NewAdhocDialogDefaults} [props.defaults]
 */
export const NewAdhocDialog = ({ close, defaults = {}, onConfirm }) => {
  const { t } = useTranslation();
  const [state, _] = useState({
    appointment: defaults.appointment ?? null,
    serviceOption: defaults.serviceOption ?? null,
  });
  const displaySnackbarMessage = useSnackbarMessage();

  const [selectedAdhocType, setSelectedAdhocType] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedService, setSelectedService] = useState(
    defaults.serviceOption ?? null,
  );
  const selectedClient = defaults.client;

  const { appointment } = state;
  const locationId = useCurrentLocation();

  // LOCATION
  const locationOptions = useLocationOptions();

  // PRODUCT
  const productFilter = useMemo(() => {
    if (selectedLocation == null || selectedClient == null) {
      return null;
    }

    return {
      company_id: { eq: selectedLocation.company_id },
      client_id: { eq: selectedClient.id },
    };
  }, [selectedLocation, selectedClient]);
  const productOptions = useProductOptions(productFilter);

  // SERVICE
  const serviceFilter = React.useMemo(() => {
    return selectedLocation == null || selectedProduct == null
      ? null
      : {
          location_id: { eq: selectedLocation.id },
          product_id: { eq: selectedProduct.id },
          create_reservation: { eq: true },
        };
  }, [selectedLocation, selectedProduct]);
  const serviceOptions = useServiceOptions(serviceFilter);

  // Set selectedProduct to braces if not selected
  useEffect(() => {
    if (productOptions?.options?.length && selectedProduct == null) {
      const bracesProduct =
        productOptions.options.find(
          (po) => po.id === "59b7da94-8ce7-4bd1-bdce-f981a72c421e", // Braces id
        ) ?? null;

      setSelectedProduct(bracesProduct);
    }
  }, [productOptions]);

  useEffect(() => {
    const location = locationOptions.options.find(
      (loc) => loc.id === locationId,
    );
    setSelectedLocation(location ?? null);
  }, [locationOptions]);

  useEffect(() => {
    if (serviceOptions.loading) {
      return;
    }
  }, [serviceOptions.loading]);

  useEffect(() => {
    if (
      (defaults.types ?? Object.keys(adhocTypes)).includes(
        adhocTypesEnum.NOSHOW,
      )
    ) {
      setSelectedAdhocType(adhocTypes[adhocTypesEnum.NOSHOW]);
    }

    if (selectedAdhocType?.value === adhocTypesEnum.INITIALEXAMINATION) {
      setSelectedService(
        serviceOptions.options.find((option) =>
          option.name.includes("Vstupní vyšetření"),
        ),
      );
    }
  }, [selectedAdhocType]);

  const { errors, loading, onSubmit } = useFormAction(
    async () => {
      const start = new Date();
      const end = new Date(start.getTime());
      end.setMinutes(end.getMinutes() + selectedService.duration);
      if (selectedAdhocType.value === adhocTypesEnum.NOSHOW) {
        api.patchAppointment({
          id: appointment.id,
          status_id: status.NOSHOW,
        });
      }
      const clientHasBlocker = hasBlocker(defaults.client);
      const res = await api.createAppointment({
        client_ids: [defaults.client.id],
        service_id: selectedService.id,
        start: d.toString(start),
        end: d.toString(end),
        status_id: clientHasBlocker ? status.CONFIRMED : status.WAITING,
        location_id: locationId,
        adhoc_type: selectedAdhocType.value,
      });
      const appointmentId = res.data.createAppointment[0].id;
      if (clientHasBlocker) {
        onConfirm?.(appointmentId);
      }
    },
    () => {
      displaySnackbarMessage({
        title:
          selectedAdhocType.value === adhocTypesEnum.NOSHOW
            ? "AD HOC rezervace byla úspěšně vytvořena a klient byl přesunutý do čekárny."
            : "Rezervace byla úspěšně vytvořena.",
        content: "",
      });
      close();
    },
  );

  return (
    <Dialog open={true} onClose={() => close()} maxWidth="sm">
      <DialogTitle>
        {t("reception.newAdhoc")}
        <IconButton aria-label="close" onClick={() => close()}>
          <FontAwesomeIcon icon={faXmark} fixedWidth />
        </IconButton>
      </DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap="1rem">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              <ClientAutocomplete disabled option={defaults.client} />
              <AutocompleteSelect
                required
                name="type"
                options={(defaults.types ?? Object.keys(adhocTypes)).map(
                  (type) => {
                    return adhocTypes[type];
                  },
                )}
                label={t("appointment.adhoc_type")}
                value={selectedAdhocType}
                disabled={selectedAdhocType?.value === adhocTypesEnum.NOSHOW}
                onChange={(value) => {
                  setSelectedAdhocType(value);
                }}
              />
              <AutocompleteSelect
                required
                loading={productOptions.loading}
                options={productOptions.options}
                label={t("appointment.product")}
                value={selectedProduct}
                onChange={(value) => {
                  setSelectedProduct(value);
                  setSelectedService(null); // Reset service when product changes
                }}
              />
              <AutocompleteSelect
                required
                name="service_id"
                loading={serviceOptions.loading}
                options={serviceOptions.options}
                label={t("appointment.service")}
                value={selectedService}
                disabled={
                  selectedAdhocType?.value === adhocTypesEnum.NOSHOW ||
                  selectedAdhocType?.value === adhocTypesEnum.INITIALEXAMINATION
                }
                onChange={(value) => {
                  setSelectedService(value);
                }}
              />
            </Box>

            <FormGroup>
              <FormControlLabel
                control={<Checkbox required />}
                label={t("reception.floorManagerApproval")}
              />
            </FormGroup>
            <FormErrorList formErrors={errors} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => close()} variant="outlined">
            {t("form.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={loading}
            sx={{ padding: "0.7rem 2.4rem", gap: "0.6rem" }}
            variant="contained"
          >
            {loading && <ButtonLoader />}
            <FontAwesomeIcon icon={faCalendarDays} />
            {t("form.adHocReserve")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
